import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Input, TextArea, Select, DatePicker, toast } from '../../components/common';
import { purchaseOrderApi, vendorApi, rfqApi } from '../../services/api';
import './PurchaseOrderForm.css';

export default function PurchaseOrderForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const quotationId = searchParams.get('quotationId');
    const queryClient = useQueryClient();
    const isEditing = !!id;

    const [form, setForm] = useState({
        vendorId: '',
        quotationId: quotationId || '',
        deliveryDate: '',
        shippingAddress: '',
        paymentTerms: '',
        notes: '',
        items: []
    });
    const [errors, setErrors] = useState({});

    // Fetch vendors
    const { data: vendors } = useQuery({
        queryKey: ['vendors', 'approved'],
        queryFn: () => vendorApi.list({ status: 'approved', limit: 100 }),
        select: (res) => res.data?.data || []
    });

    // Fetch existing PO if editing
    const { data: poData } = useQuery({
        queryKey: ['purchaseOrder', id],
        queryFn: () => purchaseOrderApi.getById(id),
        enabled: isEditing,
        select: (res) => res.data?.data
    });

    useEffect(() => {
        if (poData) {
            setForm({
                vendorId: poData.vendorId?.toString() || '',
                quotationId: poData.quotationId?.toString() || '',
                deliveryDate: poData.deliveryDate?.split('T')[0] || '',
                shippingAddress: poData.shippingAddress || '',
                paymentTerms: poData.paymentTerms || '',
                notes: poData.notes || '',
                items: poData.items || []
            });
        }
    }, [poData]);

    const saveMutation = useMutation({
        mutationFn: (data) => isEditing ? Promise.resolve() :
            (quotationId ? purchaseOrderApi.createFromQuotation(quotationId, data) : purchaseOrderApi.create(data)),
        onSuccess: (res) => {
            toast.success(`PO ${isEditing ? 'updated' : 'created'} successfully`);
            queryClient.invalidateQueries(['purchaseOrders']);
            navigate(`/purchase-orders/${res.data?.data?.id || id}`);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.vendorId) newErrors.vendorId = 'Vendor is required';
        if (!form.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
        if (!form.shippingAddress) newErrors.shippingAddress = 'Shipping address is required';
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            const errorMessages = Object.values(newErrors).join(', ');
            toast.error(`Please fix the following errors: ${errorMessages}`);
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (!validate()) return;
        saveMutation.mutate({
            ...form,
            vendorId: form.vendorId,
            quotationId: form.quotationId || null
        });
    };

    const vendorOptions = vendors?.map(v => ({ value: v.id.toString(), label: v.name })) || [];

    return (
        <div className="po-form-page">
            <PageHeader
                title={isEditing ? 'Edit Purchase Order' : 'Create Purchase Order'}
                breadcrumbs={[
                    { label: 'Purchase Orders', path: '/purchase-orders' },
                    { label: isEditing ? 'Edit' : 'Create' }
                ]}
            />

            <div className="po-form-content">
                <Card>
                    <CardBody>
                        <h3>PO Details</h3>
                        <div className="form-grid">
                            <Select
                                name="vendorId"
                                label="Vendor"
                                value={form.vendorId}
                                onChange={handleChange}
                                options={vendorOptions}
                                error={errors.vendorId}
                                required
                            />
                            <DatePicker
                                label="Delivery Date"
                                value={form.deliveryDate}
                                onChange={(date) => setForm(prev => ({ ...prev, deliveryDate: date }))}
                                minDate={new Date().toISOString().split('T')[0]}
                                error={errors.deliveryDate}
                                required
                            />
                            <div className="form-full">
                                <TextArea
                                    name="shippingAddress"
                                    label="Shipping Address"
                                    placeholder="Full shipping address"
                                    value={form.shippingAddress}
                                    onChange={handleChange}
                                    error={errors.shippingAddress}
                                    rows={2}
                                    required
                                />
                            </div>
                            <div className="form-full">
                                <Input
                                    name="paymentTerms"
                                    label="Payment Terms"
                                    placeholder="e.g., Net 30"
                                    value={form.paymentTerms}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-full">
                                <TextArea
                                    name="notes"
                                    label="Notes"
                                    placeholder="Additional notes..."
                                    value={form.notes}
                                    onChange={handleChange}
                                    rows={2}
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <div className="po-form-actions">
                    <Button variant="secondary" onClick={() => navigate('/purchase-orders')}>
                        Cancel
                    </Button>
                    <Button icon={Save} onClick={handleSave} loading={saveMutation.isPending}>
                        {isEditing ? 'Update PO' : 'Create PO'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
